/**
 * Tasks/Jobs Service
 * Handles task/job-related API operations
 */

import api from './api';

class TasksService {
  /**
   * Get all tasks/jobs with filters
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} Tasks list with pagination
   */
  async getTasks(filters = {}) {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.order) params.append('order', filters.order);
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    if (filters.date) params.append('date', filters.date);

    const response = await api.get(`/job?${params.toString()}`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch tasks');
  }

  /**
   * Get task by ID
   * @param {string} taskId - Task ID
   * @returns {Promise<Object>} Task details
   */
  async getTaskById(taskId) {
    const response = await api.get(`/job/${taskId}`);

    if (response.success && response.data) {
      return response.data.job;
    }

    throw new Error('Failed to fetch task details');
  }

  /**
   * Create new task/job
   * @param {Object} taskData - Task data
   * @returns {Promise<Object>} Created task
   */
  async createTask(taskData) {
    // Support both old format (simple strings) and new format (objects with name, address, coordinates, phone)
    const payload = {
      from: typeof taskData.from === 'string'
        ? { name: taskData.from, address: taskData.from }
        : {
            name: taskData.from.name,
            address: taskData.from.address,
            phone: taskData.from.phone || null,
            coordinates: taskData.from.coordinates,
          },
      to: typeof taskData.to === 'string'
        ? { name: taskData.to, address: taskData.to }
        : {
            name: taskData.to.name,
            address: taskData.to.address,
            phone: taskData.to.phone || null,
            coordinates: taskData.to.coordinates,
          },
      priority: taskData.priority,
      notes: taskData.notes,
      scheduledDate: taskData.scheduledDate || new Date().toISOString(),
    };

    const response = await api.post('/job', payload);

    if (response.success && response.data) {
      return response.data.job;
    }

    throw new Error('Failed to create task');
  }

  /**
   * Update task
   * @param {string} taskId - Task ID
   * @param {Object} updates - Updated data
   * @returns {Promise<Object>} Updated task
   */
  async updateTask(taskId, updates) {
    const response = await api.put(`/job/${taskId}`, updates);

    if (response.success && response.data) {
      return response.data.job;
    }

    throw new Error('Failed to update task');
  }

  /**
   * Delete task
   * @param {string} taskId - Task ID
   * @returns {Promise<Object>}
   */
  async deleteTask(taskId) {
    const response = await api.delete(`/job/${taskId}`);

    return response;
  }

  /**
   * Cancel task/job
   * @param {string} taskId - Task ID
   * @param {string} reason - Cancellation reason (optional)
   * @returns {Promise<Object>} Cancelled task
   */
  async cancelTask(taskId, reason = '') {
    const response = await api.patch(`/job/${taskId}/cancel`, { reason });

    if (response.success && response.data) {
      return response.data.job;
    }

    throw new Error(response.error?.message || 'Failed to cancel task');
  }

  /**
   * Track order/job by QR code or job ID
   * @param {string} trackingCode - QR code or job ID
   * @returns {Promise<Object>} Tracking details
   */
  async trackOrder(trackingCode) {
    const response = await api.get(`/job/track/${trackingCode}`, {
      includeAuth: false, // Public tracking endpoint
    });

    if (response.success && response.data) {
      return response.data.job;
    }

    throw new Error('Order not found');
  }

  /**
   * Generate QR code for job
   * @param {string} jobId - Job ID
   * @returns {Promise<Object>} QR code data
   */
  async generateQRCode(jobId) {
    const response = await api.post('/qr/generate', { jobId });

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to generate QR code');
  }

  /**
   * Get QR code image
   * @param {string} qrCode - QR code string
   * @param {string} format - Image format (png, svg, dataurl)
   * @returns {Promise<string>} QR code image URL or data
   */
  async getQRCodeImage(qrCode, format = 'dataurl') {
    const response = await api.get(`/qr/${qrCode}/${format}`, {
      includeAuth: false,
    });

    if (response.success && response.data) {
      return response.data.qrImage || response.data.qrCodeUrl;
    }

    throw new Error('Failed to get QR code image');
  }

  /**
   * Get today's jobs
   * @returns {Promise<Object>} Today's jobs
   */
  async getTodaysJobs() {
    const response = await api.get('/job/today');

    if (response.success && response.data) {
      return response.data.jobs;
    }

    throw new Error('Failed to fetch today\'s jobs');
  }

  /**
   * Get job statistics
   * @returns {Promise<Object>} Job stats
   */
  async getJobStats() {
    const response = await api.get('/job/stats');

    if (response.success && response.data) {
      return response.data.stats;
    }

    throw new Error('Failed to fetch job statistics');
  }

  /**
   * Export jobs to CSV
   * @param {Object} filters - Filter options
   * @returns {Promise<Blob>} CSV file blob
   */
  async exportToCSV(filters = {}) {
    const params = new URLSearchParams();

    if (filters.status) params.append('status', filters.status);
    if (filters.date) params.append('date', filters.date);
    if (filters.search) params.append('search', filters.search);

    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/job/export/csv?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${api.getToken()}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return await response.blob();
  }

  /**
   * Export jobs to Excel
   * @param {Object} filters - Filter options
   * @returns {Promise<Blob>} Excel file blob
   */
  async exportToExcel(filters = {}) {
    const params = new URLSearchParams();

    if (filters.status) params.append('status', filters.status);
    if (filters.date) params.append('date', filters.date);
    if (filters.search) params.append('search', filters.search);

    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/job/export/excel?${params.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${api.getToken()}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return await response.blob();
  }
}

const tasksService = new TasksService();
export default tasksService;
